import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { ShieldCheck, ChevronRight, ChevronLeft, AlertTriangle, ExternalLink, Sparkles, Briefcase, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { HNWI_STATEMENT, SOPHISTICATED_STATEMENT } from '../utils/fcaStatements';

const STEPS = ['route', 'criteria', 'statement', 'confirm'];

export default function InvestorCertification() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get('returnTo');

  const [stepIdx, setStepIdx] = useState(0);
  const [tier, setTier] = useState(null); // 'hnwi' | 'self_cert_sophisticated'
  const [hnwi, setHnwi] = useState({ income_over_threshold: false, assets_over_threshold: false });
  const [soph, setSoph] = useState({
    investment_network_member: false,
    multiple_unlisted_investments: false,
    finance_industry_professional: false,
    director_of_company: false,
  });
  const [acknowledged, setAcknowledged] = useState(false);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.getKycStatus().then(r => setStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.full_name) setSignature(user.full_name);
  }, [user]);

  const step = STEPS[stepIdx];

  const canAdvance = () => {
    if (step === 'route') return !!tier;
    if (step === 'criteria') {
      if (tier === 'hnwi') return hnwi.income_over_threshold || hnwi.assets_over_threshold;
      if (tier === 'self_cert_sophisticated') return Object.values(soph).some(Boolean);
    }
    if (step === 'statement') return acknowledged && signature.trim().length >= 2;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        tier,
        statement_acknowledged: acknowledged,
        signature_full_name: signature.trim(),
      };
      if (tier === 'hnwi') payload.hnwi_criteria = hnwi;
      if (tier === 'self_cert_sophisticated') payload.sophisticated_criteria = soph;
      const res = await api.certifyKyc(payload);
      setDone(res.data);
      await refreshUser?.();
      toast.success('Certification confirmed.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save your certification.');
    } finally {
      setSubmitting(false);
    }
  };

  // Already certified? Show summary instead
  if (status?.is_active && !done) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="kyc-already-certified">
          <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Investor Status</p>
          <h1 className="font-serif text-3xl text-white mb-6">You are already certified</h1>
          <div className="luxury-card p-6 mb-6">
            <div className="flex items-start gap-4">
              <ShieldCheck size={28} className="text-emerald-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <p className="text-white font-serif text-lg">
                  {status.tier === 'hnwi' ? 'Certified High Net Worth Individual' : 'Certified Sophisticated Investor'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Certified {status.certified_at && new Date(status.certified_at).toLocaleDateString('en-GB')} ·
                  {' '}Valid until {status.expires_at && new Date(status.expires_at).toLocaleDateString('en-GB')}
                </p>
                <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                  Your certification covers UK FCA Financial Promotion Order exemptions for non-readily realisable securities, including tokenised luxury assets on Assagart.
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              data-testid="kyc-revoke-btn"
              onClick={async () => {
                if (!window.confirm('Revoke your investor certification? You will lose access to tokenisation and marketplace purchases until you re-certify.')) return;
                try {
                  await api.revokeKyc();
                  toast.success('Certification revoked.');
                  await refreshUser?.();
                  setStatus({ ...status, is_active: false, tier: 'none' });
                  setStepIdx(0);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to revoke');
                }
              }}
            >
              Revoke certification
            </Button>
            <Link to="/dashboard">
              <Button className="btn-secondary">Back to Vault</Button>
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Confirmation screen
  if (done) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-10 max-w-2xl mx-auto" data-testid="kyc-confirmation">
          <div className="luxury-card p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-500/15 flex items-center justify-center">
              <ShieldCheck size={32} className="text-emerald-400" />
            </div>
            <h1 className="font-serif text-2xl text-white mb-2">Certification confirmed</h1>
            <p className="text-sm text-muted-foreground mb-6">
              You are now a {done.tier === 'hnwi' ? 'Certified High Net Worth Individual' : 'Certified Sophisticated Investor'} on Assagart.
            </p>
            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded p-4 text-left text-xs text-muted-foreground space-y-1 mb-6">
              <div className="flex justify-between"><span>Certified on</span><span className="text-white">{new Date(done.certified_at).toLocaleDateString('en-GB')}</span></div>
              <div className="flex justify-between"><span>Valid until</span><span className="text-white">{new Date(done.expires_at).toLocaleDateString('en-GB')}</span></div>
              <div className="flex justify-between"><span>Statement version</span><span className="text-white font-mono text-[10px]">FCA-FPO-2024-01-31</span></div>
            </div>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => navigate(returnTo || '/dashboard')} className="btn-primary" data-testid="kyc-continue-btn">
                {returnTo ? 'Continue to ' + returnTo : 'Go to my Vault'}
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="kyc-onboarding">
        {/* Header */}
        <div className="mb-8">
          <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Investor Certification</p>
          <h1 className="font-serif text-3xl text-white">Confirm your investor status</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Tokenised luxury assets are treated by the UK FCA as <em>non-readily realisable securities</em>. To buy, sell or tokenise on Assagart we are required to verify you qualify as a High Net Worth or Sophisticated Investor under the Financial Promotion Order.
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8" data-testid="kyc-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                i < stepIdx ? 'bg-emerald-500/20 text-emerald-400' :
                i === stepIdx ? 'bg-gold text-black' :
                'bg-[#1A1A1A] text-muted-foreground'
              }`}>{i + 1}</div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < stepIdx ? 'bg-emerald-500/30' : 'bg-[#1A1A1A]'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Route */}
        {step === 'route' && (
          <div className="space-y-3" data-testid="kyc-step-route">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Choose your certification route</p>
            <button
              onClick={() => setTier('hnwi')}
              className={`w-full text-left luxury-card p-5 transition-colors ${tier === 'hnwi' ? 'border-gold' : 'hover:border-gold/40'}`}
              data-testid="kyc-route-hnwi"
            >
              <div className="flex items-start gap-4">
                <Sparkles size={20} className="text-gold flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-serif text-lg">High Net Worth Individual</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    Annual income of £170,000+ or net assets of £430,000+ (excluding primary residence and pension).
                  </p>
                  <p className="text-[10px] text-gold mt-2">Article 48 FPO · Most common route</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setTier('self_cert_sophisticated')}
              className={`w-full text-left luxury-card p-5 transition-colors ${tier === 'self_cert_sophisticated' ? 'border-gold' : 'hover:border-gold/40'}`}
              data-testid="kyc-route-sophisticated"
            >
              <div className="flex items-start gap-4">
                <Briefcase size={20} className="text-gold flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-serif text-lg">Self-Certified Sophisticated Investor</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    For investors with material experience: business angels, multiple unlisted investments, finance professionals, or company directors.
                  </p>
                  <p className="text-[10px] text-gold mt-2">Article 50A FPO</p>
                </div>
              </div>
            </button>
            <p className="text-[11px] text-muted-foreground italic mt-4">
              Don't qualify under either? <a href="mailto:support@assagart.co.uk" className="text-gold hover:underline">Get in touch</a> - we may be able to help via a regulated wealth-manager partner.
            </p>
          </div>
        )}

        {/* Step 2: Criteria */}
        {step === 'criteria' && tier === 'hnwi' && (
          <div className="space-y-3" data-testid="kyc-step-hnwi-criteria">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">High Net Worth - confirm your circumstances</p>
            <p className="text-xs text-muted-foreground leading-relaxed mb-4">
              You must satisfy <strong className="text-white">at least one</strong> of the criteria below for the financial year immediately before today.
            </p>

            <label className="flex items-start gap-3 p-4 luxury-card cursor-pointer hover:border-gold/40 transition-colors">
              <Checkbox
                checked={hnwi.income_over_threshold}
                onCheckedChange={(v) => setHnwi({ ...hnwi, income_over_threshold: !!v })}
                className="mt-0.5"
                data-testid="kyc-hnwi-income"
              />
              <div>
                <p className="text-sm text-white">Annual income of <strong>£170,000 or more</strong></p>
                <p className="text-[11px] text-muted-foreground mt-1">Excluding pension drawdowns and one-off windfalls.</p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 luxury-card cursor-pointer hover:border-gold/40 transition-colors">
              <Checkbox
                checked={hnwi.assets_over_threshold}
                onCheckedChange={(v) => setHnwi({ ...hnwi, assets_over_threshold: !!v })}
                className="mt-0.5"
                data-testid="kyc-hnwi-assets"
              />
              <div>
                <p className="text-sm text-white">Net assets of <strong>£430,000 or more</strong></p>
                <p className="text-[11px] text-muted-foreground mt-1">Excluding primary residence, pensions, and life insurance benefits.</p>
              </div>
            </label>
          </div>
        )}

        {step === 'criteria' && tier === 'self_cert_sophisticated' && (
          <div className="space-y-3" data-testid="kyc-step-sophisticated-criteria">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Sophisticated Investor - confirm your experience</p>
            <p className="text-xs text-muted-foreground leading-relaxed mb-4">
              You must satisfy <strong className="text-white">at least one</strong> of the four criteria below.
            </p>
            {[
              { key: 'investment_network_member', label: 'Member of a business angel network', sub: 'For at least the last 6 months prior to today.' },
              { key: 'multiple_unlisted_investments', label: '2+ investments in unlisted companies', sub: 'Made in the past 2 years.' },
              { key: 'finance_industry_professional', label: 'Worked in private equity or SME finance', sub: 'In a professional capacity, within the past 2 years.' },
              { key: 'director_of_company', label: 'Director of a £1.6m+ turnover company', sub: 'Currently, or within the past 2 years.' },
            ].map(c => (
              <label key={c.key} className="flex items-start gap-3 p-4 luxury-card cursor-pointer hover:border-gold/40 transition-colors">
                <Checkbox
                  checked={soph[c.key]}
                  onCheckedChange={(v) => setSoph({ ...soph, [c.key]: !!v })}
                  className="mt-0.5"
                  data-testid={`kyc-soph-${c.key}`}
                />
                <div>
                  <p className="text-sm text-white">{c.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{c.sub}</p>
                </div>
              </label>
            ))}
          </div>
        )}

        {/* Step 3: Statement */}
        {step === 'statement' && (
          <div className="space-y-4" data-testid="kyc-step-statement">
            <div className="luxury-card p-3 bg-amber-500/5 border-amber-500/20 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-200/80 leading-relaxed">
                The text below is the FCA's prescribed statement (effective 31 January 2024). You must read and acknowledge it before signing.
              </p>
            </div>

            <div className="luxury-card p-5 max-h-[360px] overflow-y-auto" data-testid="kyc-statement-text">
              <pre className="whitespace-pre-wrap font-sans text-xs text-muted-foreground leading-relaxed">
                {tier === 'hnwi' ? HNWI_STATEMENT : SOPHISTICATED_STATEMENT}
              </pre>
            </div>

            <label className="flex items-start gap-3 p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded cursor-pointer">
              <Checkbox
                checked={acknowledged}
                onCheckedChange={(v) => setAcknowledged(!!v)}
                className="mt-0.5"
                data-testid="kyc-acknowledge"
              />
              <p className="text-xs text-white leading-relaxed">
                I have read the statement above. I confirm the answers I have given are true. I understand that the investments to which any promotions I receive may relate are likely to expose me to a significant risk of losing all of the property invested.
              </p>
            </label>

            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Signature (type your full legal name)</label>
              <Input
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                className="luxury-input mt-1"
                placeholder="e.g. Alexander James Whitcombe"
                data-testid="kyc-signature"
              />
              <p className="text-[10px] text-muted-foreground mt-1 italic">
                Your typed signature constitutes your electronic signature for the purposes of this declaration. Valid for 12 months.
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-[#1A1A1A]">
          <Button
            variant="outline"
            disabled={stepIdx === 0 || submitting}
            onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
            className="border-white/10 text-muted-foreground"
            data-testid="kyc-back-btn"
          >
            <ChevronLeft size={14} className="mr-1" /> Back
          </Button>
          {stepIdx < STEPS.length - 2 ? (
            <Button
              disabled={!canAdvance()}
              onClick={() => setStepIdx(stepIdx + 1)}
              className="btn-primary"
              data-testid="kyc-next-btn"
            >
              Continue <ChevronRight size={14} className="ml-1" />
            </Button>
          ) : (
            <Button
              disabled={!canAdvance() || submitting}
              onClick={submit}
              className="btn-primary"
              data-testid="kyc-submit-btn"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving...</> : <>Sign &amp; certify <ShieldCheck size={14} className="ml-1" /></>}
            </Button>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground italic text-center mt-6 leading-relaxed max-w-xl mx-auto">
          By certifying you do not become an Assagart customer or enter any contract. We retain a record of your declaration as required by FCA rules. You may revoke at any time. <a href="mailto:support@assagart.co.uk" className="text-gold hover:underline inline-flex items-center gap-1">Need help? <ExternalLink size={9} /></a>
        </p>
      </div>
    </DashboardLayout>
  );
}
